import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  createKnowledgePack,
  updateKnowledgePack,
  getKnowledgePack,
  publishKnowledgePack,
  ACTIVE_PACK_TYPES,
  getPackTypeIcon,
} from '../api/knowledgePacks';

/**
 * NEXUS V6.3 Phase 2B - Knowledge Pack Wizard
 * 3-Step Wizard: Create → Review → Publish
 * Auto-save every 5 seconds
 * Mobile-first 390×844, EMZI CI styling
 */
const KnowledgePackWizard = () => {
  const navigate = useNavigate();
  const { packId } = useParams(); // For edit mode
  const { user } = useAuth();

  // Wizard State
  const [step, setStep] = useState(1); // 1: Create, 2: Review, 3: Publish
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState(''); // 'saving' | 'saved' | 'error'

  // Form Data
  const [formData, setFormData] = useState({
    pack_id: null,
    name: '',
    type: 'Product',
    hard_constraints: [''],
    soft_constraints: [''],
    tags: [],
    metadata: {},
    status: 'draft',
    // Product Pack specific fields (bilingual)
    ingredients: { en: '', ms: '' },
    features: { en: '', ms: '' },
    usp: { en: '', ms: '' },
    taste: { en: '', ms: '' },
    form_price: { en: '', ms: '' },
    target_audience: { en: '', ms: '' },
  });

  // Tag input
  const [tagInput, setTagInput] = useState('');

  // RBAC Check
  const isAdmin = user?.role === 'admin';
  const isManagement = user?.role === 'management' || user?.role === 'management_functional';
  const canCreate = isAdmin || isManagement;

  // Load existing pack for edit
  useEffect(() => {
    if (packId) {
      setIsEditMode(true);
      loadPackData();
    }
  }, [packId]);

  const loadPackData = async () => {
    try {
      setLoading(true);
      const response = await getKnowledgePack(packId, user?.role);
      const pack = response.pack;
      setFormData({
        pack_id: pack.pack_id,
        name: pack.name,
        type: pack.type,
        hard_constraints: pack.hard_constraints.length > 0 ? pack.hard_constraints : [''],
        soft_constraints: pack.soft_constraints?.length > 0 ? pack.soft_constraints : [''],
        tags: pack.tags || [],
        metadata: pack.metadata || {},
        status: pack.status,
        // Load Product Pack specific fields
        ingredients: pack.metadata?.content?.ingredients || { en: '', ms: '' },
        features: pack.metadata?.content?.features || { en: '', ms: '' },
        usp: pack.metadata?.content?.usp || { en: '', ms: '' },
        taste: pack.metadata?.content?.taste || { en: '', ms: '' },
        form_price: pack.metadata?.content?.form_price || { en: '', ms: '' },
        target_audience: pack.metadata?.content?.target_audience || { en: '', ms: '' },
      });
    } catch (err) {
      console.error('Failed to load pack:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Auto-save every 5 seconds
  useEffect(() => {
    if (!formData.pack_id) return; // Only auto-save existing packs

    const interval = setInterval(() => {
      autoSave();
    }, 5000); // 5 seconds

    return () => clearInterval(interval);
  }, [formData]);

  const autoSave = async () => {
    if (!formData.pack_id || !canCreate) return;

    try {
      setAutoSaveStatus('saving');
      
      // Clean data - convert objects to strings
      const cleanArray = (arr) => arr
        .map(x => typeof x === 'string' ? x : (x?.value || x?.en || x?.ms || ''))
        .filter(Boolean);
      
      // Prepare metadata with Product Pack specific fields
      const metadata = { ...formData.metadata };
      if (formData.type === 'Product') {
        metadata.content = {
          ingredients: formData.ingredients,
          features: formData.features,
          usp: formData.usp,
          taste: formData.taste,
          form_price: formData.form_price,
          target_audience: formData.target_audience,
        };
      }
      
      await updateKnowledgePack(
        formData.pack_id,
        {
          name: typeof formData.name === 'string' ? formData.name : (formData.name?.en || formData.name?.ms || ''),
          hard_constraints: cleanArray(formData.hard_constraints),
          soft_constraints: cleanArray(formData.soft_constraints),
          tags: cleanArray(formData.tags),
          metadata: metadata,
        },
        user?.role
      );
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(''), 2000);
    } catch (err) {
      console.error('Auto-save failed:', err);
      setAutoSaveStatus('error');
      setTimeout(() => setAutoSaveStatus(''), 3000);
    }
  };

  // Form handlers
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleConstraintChange = (type, index, value) => {
    setFormData(prev => {
      const constraints = [...prev[type]];
      constraints[index] = value;
      return { ...prev, [type]: constraints };
    });
  };

  const addConstraint = (type) => {
    setFormData(prev => ({
      ...prev,
      [type]: [...prev[type], ''],
    }));
  };

  const removeConstraint = (type, index) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index),
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag),
    }));
  };

  // Step Navigation
  const handleNext = async () => {
    if (step === 1) {
      // Validate Step 1
      const nameStr = typeof formData.name === 'string' ? formData.name : (formData.name?.en || formData.name?.ms || '');
      if (!nameStr.trim()) {
        alert('Nama pack diperlukan');
        return;
      }
      const validConstraints = formData.hard_constraints.filter(c => typeof c === 'string' && c.trim());
      if (validConstraints.length === 0) {
        alert('Sekurang-kurangnya satu peraturan wajib diperlukan');
        return;
      }

      // Product Pack specific validation
      if (formData.type === 'Product') {
        if (!formData.ingredients.en?.trim() || !formData.ingredients.ms?.trim()) {
          alert('Ramuan (EN dan MS) diperlukan untuk Product Pack');
          return;
        }
        if (!formData.features.en?.trim() || !formData.features.ms?.trim()) {
          alert('Ciri-ciri (EN dan MS) diperlukan untuk Product Pack');
          return;
        }
        if (!formData.usp.en?.trim() || !formData.usp.ms?.trim()) {
          alert('USP (EN dan MS) diperlukan untuk Product Pack');
          return;
        }
      }

      // Create or update pack
      try {
        setLoading(true);
        
        // Clean data for API - convert ALL objects to plain strings
        const cleanArray = (arr) => arr
          .map(x => typeof x === 'string' ? x : (x?.value || x?.en || x?.ms || ''))
          .filter(Boolean);
        
        // Prepare metadata with Product Pack specific fields
        const metadata = { ...formData.metadata };
        if (formData.type === 'Product') {
          metadata.content = {
            ingredients: formData.ingredients,
            features: formData.features,
            usp: formData.usp,
            taste: formData.taste,
            form_price: formData.form_price,
            target_audience: formData.target_audience,
          };
        }
        
        const packData = {
          name: typeof formData.name === 'string' ? formData.name : (formData.name?.en || formData.name?.ms || ''),
          type: formData.type,
          hard_constraints: cleanArray(formData.hard_constraints),
          soft_constraints: cleanArray(formData.soft_constraints),
          tags: cleanArray(formData.tags),
          metadata: metadata,
        };
        
        if (!formData.pack_id) {
          // Create new pack
          const response = await createKnowledgePack(packData, user?.role);
          setFormData(prev => ({ ...prev, pack_id: response.pack_id }));
        } else {
          // Update existing pack
          await updateKnowledgePack(formData.pack_id, packData, user?.role);
        }
        setStep(2);
      } catch (err) {
        console.error('Failed to save pack:', err);
        alert('Data tidak dapat disimpan. Sila pastikan semua ruangan diisi dengan teks biasa.');
      } finally {
        setLoading(false);
      }
    } else if (step === 2) {
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handlePublish = async () => {
    if (!formData.pack_id) {
      alert('Pack must be created first');
      return;
    }

    const confirmPublish = window.confirm(
      'Are you sure you want to publish this Knowledge Pack? It will become active and visible to all users.'
    );

    if (!confirmPublish) return;

    try {
      setLoading(true);
      await publishKnowledgePack(
        formData.pack_id,
        {
          published_by: user?.user_id,
          publish_reason: 'Published via wizard',
        },
        user?.role
      );
      alert('Knowledge Pack published successfully!');
      navigate('/knowledge-packs');
    } catch (err) {
      console.error('Failed to publish pack:', err);
      alert(`Failed to publish pack: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      setLoading(true);
      if (formData.pack_id) {
        // Clean data - convert objects to strings
        const cleanArray = (arr) => arr
          .map(x => typeof x === 'string' ? x : (x?.value || x?.en || x?.ms || ''))
          .filter(Boolean);
        
        // Prepare metadata with Product Pack specific fields
        const metadata = { ...formData.metadata };
        if (formData.type === 'Product') {
          metadata.content = {
            ingredients: formData.ingredients,
            features: formData.features,
            usp: formData.usp,
            taste: formData.taste,
            form_price: formData.form_price,
            target_audience: formData.target_audience,
          };
        }
        
        await updateKnowledgePack(
          formData.pack_id,
          {
            name: typeof formData.name === 'string' ? formData.name : (formData.name?.en || formData.name?.ms || ''),
            hard_constraints: cleanArray(formData.hard_constraints),
            soft_constraints: cleanArray(formData.soft_constraints),
            tags: cleanArray(formData.tags),
            metadata: metadata,
          },
          user?.role
        );
      }
      alert('Draf berjaya disimpan');
      navigate('/knowledge-packs');
    } catch (err) {
      console.error('Failed to save draft:', err);
      alert('Data tidak dapat disimpan. Sila pastikan semua ruangan diisi dengan teks biasa.');
    } finally {
      setLoading(false);
    }
  };

  if (!canCreate) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-5xl mb-3">🔒</div>
          <h2 className="text-lg font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-sm text-gray-600 mb-4">You do not have permission to create knowledge packs.</p>
          <button
            onClick={() => navigate('/knowledge-packs')}
            className="px-6 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition"
          >
            Back to Packs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white" style={{ height: '100dvh' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0e67a2] to-[#0a5a8a] text-white px-4 py-3 shadow-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-lg">
              ✨
            </div>
            <div>
              <h1 className="text-lg font-black leading-tight">
                {isEditMode ? 'Edit Pack' : 'Buat Pack'}
              </h1>
              <p className="text-xs font-medium opacity-90">
                Langkah {step} dari 3: {step === 1 ? 'Buat' : step === 2 ? 'Semak' : 'Terbitkan'}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            {(isAdmin || isManagement) && (
              <span className="text-[10px] font-medium px-2 py-0.5 bg-white/20 rounded">
                🟢 ISO Compliance Active
              </span>
            )}
            {autoSaveStatus && (
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${
                autoSaveStatus === 'saved' ? 'bg-green-500/20 text-white' :
                autoSaveStatus === 'saving' ? 'bg-yellow-500/20 text-white' :
                'bg-red-500/20 text-white'
              }`}>
                {autoSaveStatus === 'saved' ? '✓ Saved' : autoSaveStatus === 'saving' ? '⏳ Saving...' : '⚠️ Error'}
              </span>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-3 bg-white/20 rounded-full h-1.5">
          <div
            className="bg-white rounded-full h-1.5 transition-all duration-300"
            style={{ width: `${(step / 3) * 100}%` }}
          />
        </div>
      </div>

      {/* Content */}
      <div className="p-4 pb-20 overflow-y-auto" style={{ maxHeight: 'calc(100dvh - 110px)' }}>
        {/* Step 1: Create */}
        {step === 1 && (
          <Step1Create
            formData={formData}
            tagInput={tagInput}
            onInputChange={handleInputChange}
            onConstraintChange={handleConstraintChange}
            onAddConstraint={addConstraint}
            onRemoveConstraint={removeConstraint}
            onTagInputChange={setTagInput}
            onAddTag={handleAddTag}
            onRemoveTag={handleRemoveTag}
          />
        )}

        {/* Step 2: Review */}
        {step === 2 && <Step2Review formData={formData} />}

        {/* Step 3: Publish */}
        {step === 3 && <Step3Publish formData={formData} />}

        {/* Navigation Buttons */}
        <div className="mt-4 space-y-2">
          {step < 3 && (
            <button
              onClick={handleNext}
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-base font-bold rounded-lg shadow-md hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {loading ? '⏳ Menyimpan...' : step === 1 ? 'Seterusnya: Semak →' : 'Seterusnya: Terbitkan →'}
            </button>
          )}

          {step === 3 && (
            <>
              <button
                onClick={handlePublish}
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-green-500 to-green-600 text-white text-base font-bold rounded-lg shadow-md hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {loading ? '⏳ Menerbitkan...' : '✓ Terbitkan'}
              </button>
              <button
                onClick={handleSaveDraft}
                disabled={loading}
                className="w-full py-2.5 bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                Simpan sebagai Draf
              </button>
            </>
          )}

          <div className="flex gap-2">
            {step > 1 && (
              <button
                onClick={handleBack}
                disabled={loading}
                className="flex-1 py-2.5 bg-gray-100 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                ← Kembali
              </button>
            )}
            <button
              onClick={() => navigate('/knowledge-packs')}
              disabled={loading}
              className="flex-1 py-2.5 bg-white border-2 border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              Batal
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Step 1: Create
// ============================================================================
const Step1Create = ({
  formData,
  tagInput,
  onInputChange,
  onConstraintChange,
  onAddConstraint,
  onRemoveConstraint,
  onTagInputChange,
  onAddTag,
  onRemoveTag,
}) => {
  return (
    <div className="space-y-3">
      {/* Pack Name */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <label className="block text-sm font-bold text-gray-900 mb-2">
          Nama Produk / Kempen <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => onInputChange('name', e.target.value)}
          placeholder="cth: Kopi Premium Arabica"
          className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Pack Type */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <label className="block text-sm font-bold text-gray-900 mb-2">
          Pack Type <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-3 gap-2">
          {ACTIVE_PACK_TYPES.map(type => (
            <button
              key={type}
              onClick={() => onInputChange('type', type)}
              className={`py-2.5 text-xs font-bold rounded-lg transition ${
                formData.type === type
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {getPackTypeIcon(type)} {type}
            </button>
          ))}
        </div>
      </div>

      {/* Hard Constraints */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-bold text-gray-900">
            Peraturan Wajib untuk AI <span className="text-red-500">*</span>
          </label>
          <button
            onClick={() => onAddConstraint('hard_constraints')}
            className="text-xs font-semibold text-blue-600 hover:text-blue-700"
          >
            + Tambah
          </button>
        </div>
        <p className="text-xs text-gray-600 mb-3">Peraturan penting yang MESTI diikuti oleh AI</p>
        <div className="space-y-2">
          {formData.hard_constraints.map((constraint, idx) => (
            <div key={`item-${idx}`} className="flex gap-2">
              <input
                type="text"
                value={constraint}
                onChange={(e) => onConstraintChange('hard_constraints', idx, e.target.value)}
                placeholder="cth: Mesti sebut Halal"
                className="flex-1 px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {formData.hard_constraints.length > 1 && (
                <button
                  onClick={() => onRemoveConstraint('hard_constraints', idx)}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Soft Constraints */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-bold text-gray-900">
            Panduan Disyorkan untuk AI
          </label>
          <button
            onClick={() => onAddConstraint('soft_constraints')}
            className="text-xs font-semibold text-blue-600 hover:text-blue-700"
          >
            + Tambah
          </button>
        </div>
        <p className="text-xs text-gray-600 mb-3">Panduan yang DISYORKAN untuk AI (pilihan)</p>
        <div className="space-y-2">
          {formData.soft_constraints.map((constraint, idx) => (
            <div key={`item-${idx}`} className="flex gap-2">
              <input
                type="text"
                value={constraint}
                onChange={(e) => onConstraintChange('soft_constraints', idx, e.target.value)}
                placeholder="cth: Guna nada mesra dan hangat"
                className="flex-1 px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {formData.soft_constraints.length > 1 && (
                <button
                  onClick={() => onRemoveConstraint('soft_constraints', idx)}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Tags */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <label className="block text-sm font-bold text-gray-900 mb-2">Tanda Kata</label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={tagInput}
            onChange={(e) => onTagInputChange(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), onAddTag())}
            placeholder="Tambah tanda kata (tekan Enter)"
            className="flex-1 px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            onClick={onAddTag}
            className="px-4 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition"
          >
            Tambah
          </button>
        </div>
        {formData.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {formData.tags.map((tag, idx) => (
              <span
                key={`item-${idx}`}
                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full"
              >
                #{tag}
                <button
                  onClick={() => onRemoveTag(tag)}
                  className="text-blue-600 hover:text-blue-800 font-bold"
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Product Pack Specific Fields */}
      {formData.type === 'Product' && (
        <>
          {/* Ingredients */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <label className="block text-sm font-bold text-gray-900 mb-2">
              Ramuan / Ingredients <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-600 mb-2">Senaraikan ramuan produk (dwibahasa)</p>
            <div className="space-y-2">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Bahasa Melayu</label>
                <textarea
                  value={formData.ingredients?.ms || ''}
                  onChange={(e) => onInputChange('ingredients', { ...formData.ingredients, ms: e.target.value })}
                  placeholder="cth: Kopi Arabica, Gula Aren, Creamer"
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">English</label>
                <textarea
                  value={formData.ingredients?.en || ''}
                  onChange={(e) => onInputChange('ingredients', { ...formData.ingredients, en: e.target.value })}
                  placeholder="e.g., Arabica Coffee, Palm Sugar, Creamer"
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={2}
                />
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <label className="block text-sm font-bold text-gray-900 mb-2">
              Ciri-ciri / Features <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-600 mb-2">Ciri-ciri utama produk (dwibahasa)</p>
            <div className="space-y-2">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Bahasa Melayu</label>
                <textarea
                  value={formData.features?.ms || ''}
                  onChange={(e) => onInputChange('features', { ...formData.features, ms: e.target.value })}
                  placeholder="cth: Tenaga segera, Gula sihat, Mesra perut"
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">English</label>
                <textarea
                  value={formData.features?.en || ''}
                  onChange={(e) => onInputChange('features', { ...formData.features, en: e.target.value })}
                  placeholder="e.g., Instant energy, Healthy sugar, Stomach-friendly"
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={2}
                />
              </div>
            </div>
          </div>

          {/* USP */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <label className="block text-sm font-bold text-gray-900 mb-2">
              USP (Unique Selling Point) <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-600 mb-2">Apa yang istimewa tentang produk ini? (dwibahasa)</p>
            <div className="space-y-2">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Bahasa Melayu</label>
                <textarea
                  value={formData.usp?.ms || ''}
                  onChange={(e) => onInputChange('usp', { ...formData.usp, ms: e.target.value })}
                  placeholder="cth: Gula aren organik dari Indonesia, tiada bahan kimia"
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">English</label>
                <textarea
                  value={formData.usp?.en || ''}
                  onChange={(e) => onInputChange('usp', { ...formData.usp, en: e.target.value })}
                  placeholder="e.g., Organic palm sugar from Indonesia, no chemicals"
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={2}
                />
              </div>
            </div>
          </div>

          {/* Taste (Optional) */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <label className="block text-sm font-bold text-gray-900 mb-2">
              Rasa / Taste
            </label>
            <p className="text-xs text-gray-600 mb-2">Profil rasa produk (pilihan, dwibahasa)</p>
            <div className="space-y-2">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Bahasa Melayu</label>
                <input
                  type="text"
                  value={formData.taste?.ms || ''}
                  onChange={(e) => onInputChange('taste', { ...formData.taste, ms: e.target.value })}
                  placeholder="cth: Manis semulajadi, pahit sederhana"
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">English</label>
                <input
                  type="text"
                  value={formData.taste?.en || ''}
                  onChange={(e) => onInputChange('taste', { ...formData.taste, en: e.target.value })}
                  placeholder="e.g., Naturally sweet, moderate bitter"
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Form/Price (Optional) */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <label className="block text-sm font-bold text-gray-900 mb-2">
              Bentuk & Harga / Form & Price
            </label>
            <p className="text-xs text-gray-600 mb-2">Bentuk produk dan harga (pilihan, dwibahasa)</p>
            <div className="space-y-2">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Bahasa Melayu</label>
                <input
                  type="text"
                  value={formData.form_price?.ms || ''}
                  onChange={(e) => onInputChange('form_price', { ...formData.form_price, ms: e.target.value })}
                  placeholder="cth: Sachet 20g x 10 pek, RM29.90"
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">English</label>
                <input
                  type="text"
                  value={formData.form_price?.en || ''}
                  onChange={(e) => onInputChange('form_price', { ...formData.form_price, en: e.target.value })}
                  placeholder="e.g., Sachet 20g x 10 packs, RM29.90"
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Target Audience (Optional) */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <label className="block text-sm font-bold text-gray-900 mb-2">
              Sasaran Pelanggan / Target Audience
            </label>
            <p className="text-xs text-gray-600 mb-2">Siapa sasaran pelanggan? (pilihan, dwibahasa)</p>
            <div className="space-y-2">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Bahasa Melayu</label>
                <input
                  type="text"
                  value={formData.target_audience?.ms || ''}
                  onChange={(e) => onInputChange('target_audience', { ...formData.target_audience, ms: e.target.value })}
                  placeholder="cth: Wanita 25-45 tahun, kesihatan & wellness"
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">English</label>
                <input
                  type="text"
                  value={formData.target_audience?.en || ''}
                  onChange={(e) => onInputChange('target_audience', { ...formData.target_audience, en: e.target.value })}
                  placeholder="e.g., Women 25-45 years, health & wellness"
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ============================================================================
// Step 2: Review
// ============================================================================
const Step2Review = ({ formData }) => {
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl p-4 shadow-md">
        <div className="flex items-center gap-3">
          <div className="text-3xl">{getPackTypeIcon(formData.type)}</div>
          <div>
            <h2 className="text-lg font-black">{formData.name}</h2>
            <p className="text-xs font-medium opacity-90">{formData.type} Pack</p>
          </div>
        </div>
      </div>

      {/* Hard Constraints */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="text-sm font-bold text-gray-900 mb-2">Peraturan Wajib untuk AI</h3>
        <div className="space-y-1.5">
          {formData.hard_constraints.filter(c => c.trim()).map((constraint, idx) => (
            <div key={`item-${idx}`} className="flex items-start gap-2">
              <span className="text-red-600 font-bold flex-shrink-0">✓</span>
              <p className="text-xs text-gray-700">{constraint}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Soft Constraints */}
      {formData.soft_constraints.filter(c => c.trim()).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="text-sm font-bold text-gray-900 mb-2">Panduan Disyorkan untuk AI</h3>
          <div className="space-y-1.5">
            {formData.soft_constraints.filter(c => c.trim()).map((constraint, idx) => (
              <div key={`item-${idx}`} className="flex items-start gap-2">
                <span className="text-orange-500 font-bold flex-shrink-0">•</span>
                <p className="text-xs text-gray-700">{constraint}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tags */}
      {formData.tags.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="text-sm font-bold text-gray-900 mb-2">Tanda Kata</h3>
          <div className="flex flex-wrap gap-1.5">
            {formData.tags.map((tag, idx) => (
              <span key={`item-${idx}`} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                #{tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Product Pack Specific Fields */}
      {formData.type === 'Product' && (
        <>
          {/* Ingredients */}
          {(formData.ingredients?.en || formData.ingredients?.ms) && (
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h3 className="text-sm font-bold text-gray-900 mb-2">Ramuan / Ingredients</h3>
              <div className="space-y-2">
                {formData.ingredients?.ms && (
                  <div>
                    <p className="text-xs font-semibold text-gray-700 mb-0.5">BM:</p>
                    <p className="text-xs text-gray-600">{formData.ingredients.ms}</p>
                  </div>
                )}
                {formData.ingredients?.en && (
                  <div>
                    <p className="text-xs font-semibold text-gray-700 mb-0.5">EN:</p>
                    <p className="text-xs text-gray-600">{formData.ingredients.en}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Features */}
          {(formData.features?.en || formData.features?.ms) && (
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h3 className="text-sm font-bold text-gray-900 mb-2">Ciri-ciri / Features</h3>
              <div className="space-y-2">
                {formData.features?.ms && (
                  <div>
                    <p className="text-xs font-semibold text-gray-700 mb-0.5">BM:</p>
                    <p className="text-xs text-gray-600">{formData.features.ms}</p>
                  </div>
                )}
                {formData.features?.en && (
                  <div>
                    <p className="text-xs font-semibold text-gray-700 mb-0.5">EN:</p>
                    <p className="text-xs text-gray-600">{formData.features.en}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* USP */}
          {(formData.usp?.en || formData.usp?.ms) && (
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h3 className="text-sm font-bold text-gray-900 mb-2">USP</h3>
              <div className="space-y-2">
                {formData.usp?.ms && (
                  <div>
                    <p className="text-xs font-semibold text-gray-700 mb-0.5">BM:</p>
                    <p className="text-xs text-gray-600">{formData.usp.ms}</p>
                  </div>
                )}
                {formData.usp?.en && (
                  <div>
                    <p className="text-xs font-semibold text-gray-700 mb-0.5">EN:</p>
                    <p className="text-xs text-gray-600">{formData.usp.en}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Taste */}
          {(formData.taste?.en || formData.taste?.ms) && (
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h3 className="text-sm font-bold text-gray-900 mb-2">Rasa / Taste</h3>
              <div className="space-y-1">
                {formData.taste?.ms && <p className="text-xs text-gray-600">BM: {formData.taste.ms}</p>}
                {formData.taste?.en && <p className="text-xs text-gray-600">EN: {formData.taste.en}</p>}
              </div>
            </div>
          )}

          {/* Form/Price */}
          {(formData.form_price?.en || formData.form_price?.ms) && (
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h3 className="text-sm font-bold text-gray-900 mb-2">Bentuk & Harga / Form & Price</h3>
              <div className="space-y-1">
                {formData.form_price?.ms && <p className="text-xs text-gray-600">BM: {formData.form_price.ms}</p>}
                {formData.form_price?.en && <p className="text-xs text-gray-600">EN: {formData.form_price.en}</p>}
              </div>
            </div>
          )}

          {/* Target Audience */}
          {(formData.target_audience?.en || formData.target_audience?.ms) && (
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h3 className="text-sm font-bold text-gray-900 mb-2">Sasaran Pelanggan / Target Audience</h3>
              <div className="space-y-1">
                {formData.target_audience?.ms && <p className="text-xs text-gray-600">BM: {formData.target_audience.ms}</p>}
                {formData.target_audience?.en && <p className="text-xs text-gray-600">EN: {formData.target_audience.en}</p>}
              </div>
            </div>
          )}
        </>
      )}

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-xs font-semibold text-blue-900 mb-1">📝 Review Checklist:</p>
        <ul className="text-xs text-blue-800 space-y-0.5">
          <li>✓ Pack name is clear and descriptive</li>
          <li>✓ All hard constraints are critical and non-negotiable</li>
          <li>✓ Soft constraints provide helpful guidance</li>
          <li>✓ Tags are relevant for searchability</li>
          {formData.type === 'Product' && (
            <li>✓ Product details (ingredients, features, USP) are complete</li>
          )}
        </ul>
      </div>
    </div>
  );
};

// ============================================================================
// Step 3: Publish
// ============================================================================
const Step3Publish = ({ formData }) => {
  return (
    <div className="space-y-3">
      {/* Success Icon */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl p-6 text-center shadow-md">
        <div className="text-5xl mb-3">🎉</div>
        <h2 className="text-lg font-black mb-1">Bersedia untuk Diterbitkan!</h2>
        <p className="text-xs font-medium opacity-90">
          Pack anda sudah bersedia untuk digunakan
        </p>
      </div>

      {/* Pack Summary */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="text-3xl">{getPackTypeIcon(formData.type)}</div>
          <div>
            <h3 className="text-base font-black text-gray-900">{formData.name}</h3>
            <p className="text-xs text-gray-600">{formData.type} Pack</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-200">
          <div className="text-center">
            <div className="text-2xl font-black text-red-600">
              {formData.hard_constraints.filter(c => c.trim()).length}
            </div>
            <div className="text-xs font-medium text-gray-600">Peraturan Wajib</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-black text-orange-500">
              {formData.soft_constraints.filter(c => c.trim()).length}
            </div>
            <div className="text-xs font-medium text-gray-600">Panduan Disyorkan</div>
          </div>
        </div>
      </div>

      {/* What happens next */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h3 className="text-sm font-bold text-blue-900 mb-2">📢 Apa yang akan berlaku?</h3>
        <ul className="text-xs text-blue-800 space-y-1">
          <li>✓ Status pack bertukar kepada <strong>AKTIF</strong></li>
          <li>✓ Semua pengguna boleh guna pack ini</li>
          <li>✓ AI coaching akan ikut peraturan ini</li>
          <li>✓ SOA Drill akan gunakan konteks ini</li>
          <li>✓ Log aktiviti akan rekod tindakan ini</li>
        </ul>
      </div>

      {/* Warning */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
        <p className="text-xs font-semibold text-yellow-900 mb-1">⚠️ Penting:</p>
        <p className="text-xs text-yellow-800">
          Setelah diterbitkan, pack ini akan kelihatan kepada semua pengguna. Anda masih boleh edit atau rollback jika perlu.
        </p>
      </div>
    </div>
  );
};

export default KnowledgePackWizard;